import { FC, ReactNode, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { PremiumPrice } from 'src/api/internal/schema';
import { CommitmentUpsellSource } from 'src/api/tracking/types';
import { TrackingApi } from 'src/api';
import { Button, SvgHelperWrapper } from 'src/components';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { getPlanOffer } from '../utils/getPlanOffer';
import { getPlansPageSource } from '../utils/getPlansPageSource';
import Star from '../assets/star.svg';
import * as S from './styles';

const LOCATION = 'commitment_upsell' as const;
const STAR_KEYS = ['star-1', 'star-2', 'star-3', 'star-4', 'star-5'];
const STILL_FRAME_SECONDS = 0.5;

const revealStillFrame = (video: HTMLVideoElement) => {
  const seekToFrame = () => {
    if (Math.abs(video.currentTime - STILL_FRAME_SECONDS) > 0.05) {
      video.currentTime = STILL_FRAME_SECONDS;
    }
  };

  video.addEventListener(
    'seeked',
    () => {
      video.pause();
    },
    { once: true },
  );

  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    seekToFrame();
    return;
  }

  video.addEventListener('loadedmetadata', seekToFrame, { once: true });
};

const msgs = defineMessages({
  headline: {
    id: 'commitment_paywall_headline',
    defaultMessage: 'Join 250k PRO learners{br}who’ve committed',
  },
  oneMonth: {
    id: 'commitment_led_upsell_1_month',
    defaultMessage: '1 month',
  },
  twelveMonths: {
    id: 'new_plans_card_12_months',
    defaultMessage: '12 months',
  },
  lifetime: {
    id: 'ea_plans_lifetime',
  },
  mostPopular: {
    id: 'onboarding_upsell_all_pro_plans_badge_popular_plan',
    defaultMessage: 'Most popular',
  },
  save: {
    id: 'new_plans_card_save',
    defaultMessage: 'Save {percent}%',
  },
  flexible: {
    id: 'commitment_paywall_flexible',
    defaultMessage: 'Flexible, cancel anytime',
  },
  billedOnce: {
    id: 'commitment_paywall_billed_once',
    defaultMessage: 'Billed {price} once a year',
  },
  onePayment: {
    id: 'commitment_paywall_one_payment',
    defaultMessage: 'One payment, yours forever',
  },
  perMonth: {
    id: 'commitment_paywall_per_month',
    defaultMessage: 'per month',
  },
  oneTime: {
    id: 'commitment_paywall_one_time',
    defaultMessage: 'one-time',
  },
  benefitSpeaking: {
    id: 'commitment_paywall_benefit_speaking',
    defaultMessage: 'Unlimited speaking practice',
  },
  benefitSpeakingNote: {
    id: 'commitment_paywall_benefit_speaking_note',
    defaultMessage: '(limited to one session per day on the free plan)',
  },
  benefitWordlists: {
    id: 'commitment_paywall_benefit_wordlists',
    defaultMessage: 'Unlock all wordlists',
  },
  benefitAdFree: {
    id: 'commitment_paywall_benefit_ad_free',
    defaultMessage: 'Ad-free',
  },
  cta: {
    id: 'commitment_paywall_cta',
    defaultMessage: 'Continue to checkout',
  },
  reassuranceMonth: {
    id: 'commitment_paywall_reassurance_month',
    defaultMessage: 'Cancel anytime · Secure checkout · Renews monthly at {price}',
  },
  reassuranceYear: {
    id: 'commitment_paywall_reassurance_year',
    defaultMessage: 'Cancel anytime · Secure checkout · Renews yearly at {price}',
  },
  reassuranceLifetime: {
    id: 'commitment_paywall_reassurance_lifetime',
    defaultMessage: 'Secure checkout · One payment of {price}',
  },
  notReady: {
    id: 'commitment_paywall_not_ready',
    defaultMessage: 'Not ready?',
  },
  keepLearning: {
    id: 'commitment_paywall_keep_learning',
    defaultMessage: 'Keep learning for free',
  },
  quoteLead: {
    id: 'commitment_paywall_quote_lead',
    defaultMessage: '“I just had an honest-to-god conversation in French with a friend in Quebec!',
  },
  quoteBody: {
    id: 'commitment_paywall_quote_body',
    defaultMessage:
      'Memrise PRO helped me practice speaking. It honed my pronunciation, sentence structure and confidence.”',
  },
  quoteAuthor: {
    id: 'commitment_paywall_quote_author',
    defaultMessage: 'Emma · PRO user',
  },
});

export type CommitmentPaywallProps = {
  prices: PremiumPrice[];
  activePrice: PremiumPrice | null;
  onSelectPrice: (price: PremiumPrice) => void;
  onCheckout: () => void;
  onClose?: () => void;
  stillSrc?: string;
  source?: CommitmentUpsellSource;
};

export const CommitmentPaywall: FC<CommitmentPaywallProps> = ({
  prices,
  activePrice,
  onSelectPrice,
  onCheckout,
  onClose,
  stillSrc,
  source,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const isPro = !!user?.isPro;
  const stillRef = useRef<HTMLVideoElement>(null);
  const monthlyFinalPrice = prices.find(price => price.paymentPeriod === 'month')?.finalPrice;
  const offers = useMemo(
    () =>
      new Map(prices.map(price => [price.paymentPeriod, getPlanOffer(price, monthlyFinalPrice)])),
    [prices, monthlyFinalPrice],
  );
  useEffect(() => {
    const video = stillRef.current;
    if (!video || !stillSrc) return;
    revealStillFrame(video);
  }, [stillSrc]);

  useEffect(() => {
    void TrackingApi.trackUserAction('screen_viewed', LOCATION, languagePairId, isPro, {
      source: source ?? getPlansPageSource(),
      step: 'paywall',
    });
    // Fire only once when the paywall opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatAmount = (amount: number, currency: string, forceDecimals = false) =>
    intl.formatNumber(amount, {
      style: 'currency',
      currency,
      minimumFractionDigits: forceDecimals || !Number.isInteger(amount) ? 2 : 0,
      maximumFractionDigits: 2,
    });

  const planLabel = (price: PremiumPrice) => {
    if (price.paymentPeriod === 'month') return intl.formatMessage(msgs.oneMonth);
    if (price.paymentPeriod === 'year') return intl.formatMessage(msgs.twelveMonths);
    return intl.formatMessage(msgs.lifetime);
  };

  const planDetail = (price: PremiumPrice, comparisonTotal: number | null) => {
    if (price.paymentPeriod === 'year') {
      const salePrice = formatAmount(price.finalPrice, price.currency);
      return intl.formatMessage(msgs.billedOnce, {
        price:
          comparisonTotal == null ? (
            salePrice
          ) : (
            <>
              <S.StruckPrice>{formatAmount(comparisonTotal, price.currency)}</S.StruckPrice>{' '}
              {salePrice}
            </>
          ),
      });
    }
    if (price.paymentPeriod === 'lifetime') return intl.formatMessage(msgs.onePayment);
    return intl.formatMessage(msgs.flexible);
  };

  const displayAmount = (price: PremiumPrice) => {
    if (price.paymentPeriod === 'year') return price.finalPrice / 12;
    return price.finalPrice;
  };

  const amountSuffix = (price: PremiumPrice) =>
    price.paymentPeriod === 'lifetime'
      ? intl.formatMessage(msgs.oneTime)
      : intl.formatMessage(msgs.perMonth);

  const reassurance = (() => {
    if (!activePrice) return null;
    const price = formatAmount(activePrice.finalPrice, activePrice.currency);
    if (activePrice.paymentPeriod === 'month') {
      return intl.formatMessage(msgs.reassuranceMonth, { price });
    }
    if (activePrice.paymentPeriod === 'year') {
      return intl.formatMessage(msgs.reassuranceYear, { price });
    }
    return intl.formatMessage(msgs.reassuranceLifetime, { price });
  })();

  const benefits: { id: string; label: ReactNode }[] = [
    {
      id: 'speaking',
      label: (
        <>
          {intl.formatMessage(msgs.benefitSpeaking)}{' '}
          <S.BenefitNote>{intl.formatMessage(msgs.benefitSpeakingNote)}</S.BenefitNote>
        </>
      ),
    },
    { id: 'wordlists', label: intl.formatMessage(msgs.benefitWordlists) },
    { id: 'ad-free', label: intl.formatMessage(msgs.benefitAdFree) },
  ];

  return (
    <S.Page $hasMedia={!!stillSrc} data-testid="commitment-paywall">
      {stillSrc ? (
        <S.Media>
          <S.Still
            ref={stillRef}
            src={`${stillSrc}#t=${STILL_FRAME_SECONDS}`}
            muted
            playsInline
            preload="auto"
            autoPlay={false}
            controls={false}
            loop={false}
            tabIndex={-1}
            aria-hidden
            data-testid="commitment-paywall-still"
          />
          <S.Quote>
            <S.Stars>
              {STAR_KEYS.map(key => (
                <SvgHelperWrapper Svg={Star} key={key} />
              ))}
            </S.Stars>
            <S.QuoteLead>{intl.formatMessage(msgs.quoteLead)}</S.QuoteLead>
            <S.QuoteBody>{intl.formatMessage(msgs.quoteBody)}</S.QuoteBody>
            <S.QuoteAuthor>{intl.formatMessage(msgs.quoteAuthor)}</S.QuoteAuthor>
          </S.Quote>
        </S.Media>
      ) : null}

      <S.Panel>
        <S.PanelContent>
          <S.Headline>
            {intl.formatMessage(msgs.headline, {
              br: <br key="br" />,
            })}
          </S.Headline>

          <S.PlanList aria-label={intl.formatMessage(msgs.headline, { br: ' ' })}>
            {prices.map(price => {
              const selected = activePrice?.paymentPeriod === price.paymentPeriod;
              const isYearly = price.paymentPeriod === 'year';
              const offer = offers.get(price.paymentPeriod);

              return (
                <S.PlanOption
                  key={price.key || price.paymentPeriod}
                  type="button"
                  $selected={selected}
                  onClick={() => onSelectPrice(price)}
                  data-testid="price-card"
                  aria-pressed={selected}
                >
                  {isYearly ? (
                    <S.PopularBadge>{intl.formatMessage(msgs.mostPopular)}</S.PopularBadge>
                  ) : null}
                  <S.Radio $selected={selected} aria-hidden>
                    {selected ? <S.RadioCheck name="check" /> : null}
                  </S.Radio>
                  <S.PlanCopy>
                    <S.PlanHeading>
                      <S.PlanLabel>{planLabel(price)}</S.PlanLabel>
                      {offer?.savePercent != null ? (
                        <S.SaveBadge>
                          {intl.formatMessage(msgs.save, { percent: offer.savePercent })}
                        </S.SaveBadge>
                      ) : null}
                    </S.PlanHeading>
                    <S.PlanDetail>{planDetail(price, offer?.comparisonTotal ?? null)}</S.PlanDetail>
                  </S.PlanCopy>
                  <S.PriceColumn>
                    {offer?.comparisonAmount != null && !isYearly ? (
                      <S.StruckPrice>
                        {formatAmount(offer.comparisonAmount, price.currency)}
                      </S.StruckPrice>
                    ) : null}
                    <S.PlanPrice>
                      {formatAmount(displayAmount(price), price.currency, isYearly)}
                    </S.PlanPrice>
                    <S.PriceSuffix>{amountSuffix(price)}</S.PriceSuffix>
                  </S.PriceColumn>
                </S.PlanOption>
              );
            })}
          </S.PlanList>

          <S.Benefits>
            {benefits.map(benefit => (
              <S.Benefit key={benefit.id}>
                <S.BenefitCheck name="check" aria-hidden />
                <S.BenefitCopy>{benefit.label}</S.BenefitCopy>
              </S.Benefit>
            ))}
          </S.Benefits>

          <Button
            buttonType="primary"
            buttonSize="large"
            wide
            onClick={onCheckout}
            data-testid="subscribe"
            disabled={!activePrice}
          >
            {intl.formatMessage(msgs.cta)}
          </Button>

          {reassurance ? <S.Reassurance>{reassurance}</S.Reassurance> : null}

          {onClose ? (
            <S.NotReady>
              {intl.formatMessage(msgs.notReady)}{' '}
              <S.KeepLearning type="button" onClick={onClose} data-testid="keep-learning-for-free">
                {intl.formatMessage(msgs.keepLearning)}
              </S.KeepLearning>
            </S.NotReady>
          ) : null}
        </S.PanelContent>
      </S.Panel>
    </S.Page>
  );
};
